import { useEffect, useState } from "react";
import { t } from "../i18n";
import { useNavigate } from "react-router-dom";
import { CheckCheck, Cake, Heart, Clock, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useModals } from "../context/ModalsContext";
import { Shell } from "../components/Shell";
import { Avatar, Flag } from "../components/Avatar";
import { fmtRelativo } from "../lib/data";

const ICON = { cumple: Cake, aniversario: Heart, sin_contacto: Clock, cita: CalendarClock };
const tituloAlerta = (r) => {
  const d = r.datos || {};
  if (!d.nombre) return r.titulo;
  if (r.tipo.startsWith("cumple")) return t(`alerts.${r.tipo}`, { n: d.nombre }) + (d.anios ? t("alerts.turns", { a: d.anios }) : "");
  if (r.tipo === "aniversario") return t("alerts.aniversario", { n: d.nombre, a: d.anios });
  if (r.tipo === "sin_contacto") return t("alerts.sin_contacto", { n: d.nombre, d: d.dias });
  if (r.tipo === "cita") return t("alerts.cita", { n: d.nombre, h: d.hora, l: d.lugar || t("alerts.agreedPlace") });
  return r.titulo;
};
const iconFor = (tipo) => ICON[tipo.startsWith("cumple") ? "cumple" : tipo] || Clock;

const group = (items) => {
  const now = new Date(); const start = new Date(now); start.setHours(0, 0, 0, 0);
  const week = new Date(start); week.setDate(week.getDate() - 7);
  const g = { "alerts.today": [], "alerts.thisWeek": [], "alerts.earlier": [] };
  items.forEach((r) => { const d = new Date(r.fecha_disparo); (d >= start ? g["alerts.today"] : d >= week ? g["alerts.thisWeek"] : g["alerts.earlier"]).push(r); });
  return Object.entries(g).filter(([, v]) => v.length);
};

export default function Alertas() {
  const navigate = useNavigate();
  const { bump } = useModals();
  const [items, setItems] = useState(null);
  const load = () => api.get("/reminders").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const leerTodos = async () => {
    try { await api.post("/reminders/leer-todos"); toast.success(t("alerts.allRead")); await load(); bump(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const open = async (r) => {
    if (!r.leido) { api.post(`/reminders/${r.id}/leer`).then(() => bump()).catch(() => {}); }
    navigate(r.tipo === "cita" ? "/calendario" : `/personas/${r.person_id}`);
  };
  const noLeidas = items?.filter((r) => !r.leido).length || 0;

  return (
    <Shell title={t("alerts.title")} fab={false} right={noLeidas > 0 && (
      <button data-testid="mark-all-read-btn" onClick={leerTodos} className="p-2 rounded-full hover:bg-secondary text-[#d4af37]" title={t("alerts.markAll")}><CheckCheck size={20} /></button>
    )}>
      {items === null && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
      {items?.length === 0 && <div data-testid="alertas-empty" className="surface p-8 text-center text-sm text-muted-foreground mt-6">{t("alerts.empty")}</div>}
      {noLeidas > 0 && <button data-testid="mark-all-read-text-btn" onClick={leerTodos} className="text-xs text-[#f3c649] mb-3">{t("alerts.markAllN", { n: noLeidas })}</button>}
      <div className="space-y-5" data-testid="alertas-list">
        {items && group(items).map(([label, list]) => (
          <section key={label}>
            <p className="label">{t(label)}</p>
            <ul className="space-y-2">
              {list.map((r) => { const Icon = iconFor(r.tipo); return (
                <li key={r.id}><button data-testid={`reminder-${r.id}`} onClick={() => open(r)} className={`surface w-full p-3 flex items-center gap-3 text-left ${r.leido ? "opacity-60" : "border-[#d4af37]/40"}`}>
                  <Avatar nombre={r.persona?.nombre} src={r.persona?.foto_url} size={44} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm flex items-center gap-2"><Icon size={14} className="text-[#d4af37] shrink-0" /><span data-testid="reminder-titulo" className="truncate">{tituloAlerta(r)}</span><Flag iso={r.persona?.pais_iso} size={14} /></p>
                    <p className="text-xs text-muted-foreground">{fmtRelativo(r.fecha_disparo)}</p>
                  </div>
                  {!r.leido && <span data-testid="reminder-unread-dot" className="w-2 h-2 rounded-full bg-[#d4af37]" />}
                </button></li>); })}
            </ul>
          </section>
        ))}
      </div>
    </Shell>
  );
}
